/**
 * ===========================================================================
 *  NUSANTARA WARRIOR — ROSTER: the warriors and the battlefields
 * ===========================================================================
 *
 * The data behind the front end's two choices, and the one function that
 * makes a choice real. `src/ui/select.js` draws these; nothing here knows
 * what a DOM element is.
 *
 * ---------------------------------------------------------------------------
 * THE STAT BARS ARE MEASURED, NOT WRITTEN
 * ---------------------------------------------------------------------------
 * A character-select screen whose numbers are hand-typed is a decoration: it
 * tells the player a story the game does not honour. Every bar below is
 * DERIVED from `src/combat/moveset.js` — the same table the combat resolver
 * reads — so the bars re-measure themselves the moment anyone retimes a clip
 * or retunes a hitbox:
 *
 *   Attack   light-chain damage per second   sum(damage) / (sum(frames)/60)
 *   Reach    the longest swept radius        max(arc.r1) over the chain
 *   Speed    how fast the chain cycles       60 / mean(total frames)
 *   Sweep    men caught per swing            max(arc.half x arc.r1), the
 *                                            angular footprint of one blow
 *   Musou    total damage of one full musou  pulses x pulse damage
 *
 * Each measurement is then mapped onto a FIXED band (BANDS below), never
 * min/maxed across the three sets. Normalising against the roster itself is
 * how a stat screen starts lying: the three sets' light chains land within 9 %
 * of each other on damage, and a min/max stretch renders that 9 % as a
 * seven-point bar gap — "Sekar Taji hits for a third of Arya Bhumi", which is
 * false.
 * Against a fixed band the attack bars come out 7.0 / 6.7 / 6.0, which is the
 * truth: they hit about equally hard and differ in reach, speed and footprint.
 *
 * ---------------------------------------------------------------------------
 * WHAT AN ARENA CAN AND CANNOT CHANGE
 * ---------------------------------------------------------------------------
 * The valley is one hand-authored heightfield (`src/world/layout.js`) carved
 * once at boot; the river, the roads and the seven holdings do not move, and
 * nothing here pretends otherwise. What a stage DOES own is everything the
 * engine can change without a rebuild, which is most of what a Dynasty
 * Warriors stage actually is:
 *
 *   mood        the whole lighting + sky + colour-grade preset, so Kelud at
 *               dusk and Trowulan at night are different battles to
 *               look at (src/render/lighting.js MOODS)
 *   spawn       where in the valley the hero lands, which decides which
 *               holdings are near and therefore what the battle is about
 *   pressure    the reinforcement rate multiplier the director runs at
 *   foeHp       officer health multiplier
 *   seed        the battle RNG, so two runs of one stage are not identical
 *
 * ---------------------------------------------------------------------------
 * PUBLIC API
 * ---------------------------------------------------------------------------
 *   HEROES, STAGES, ARENAS      the tables (STAGES are fought on ARENAS)
 *   heroById(id), stageById(id) lookup, never null (falls back to index 0)
 *   statsFor(movesetName)       the five measured bars, 0..10
 *   previewHero(world, hero)    swap the live hero's steel + moveset
 *   applyLoadout(world, heroId, arenaId)  commit both choices
 */
import { MOVESETS } from '../combat/moveset.js';
import { SITES, ARENA_ID } from '../world/layout.js';
import { ARENA_DEFS } from '../world/arenas.js';
import { FOE } from './mission.js';

/* ========================================================== measured stats */

const STAT_KEYS = ['attack', 'reach', 'speed', 'sweep', 'musou'];
const STAT_LABEL = { attack: 'Attack', reach: 'Reach', speed: 'Speed', sweep: 'Sweep', musou: 'Musou' };

/** Raw, unnormalised measurements for one moveset. */
function measure(setName) {
  const S = MOVESETS[setName];
  if (!S) return { attack: 0, reach: 0, speed: 0, sweep: 0, musou: 0 };

  let dmg = 0, frames = 0, n = 0, reach = 0, sweep = 0;
  for (let i = 0; i < S.chain.length; i++) {
    const m = S.moves[S.chain[i]];
    if (!m) continue;
    n++;
    dmg += m.damage || 0;
    frames += m.total || (m.windup + m.active + m.recovery) || 0;
    const a = m.arc || {};
    if (a.r1 > reach) reach = a.r1;
    // half-angle in radians x radius = the arc length the blade travels, which
    // is what actually decides how many men one swing catches
    const foot = ((a.half || 0) * Math.PI / 180) * (a.r1 || 0);
    if (foot > sweep) sweep = foot;
  }

  const secs = frames / 60;
  const mu = S.musou || {};
  const pulse = mu.pulse || {};
  const pulses = pulse.every ? Math.floor((mu.duration || 0) * 60 / pulse.every) : 0;

  return {
    attack: secs > 0 ? dmg / secs : 0,
    reach,
    speed: n > 0 ? 60 / (frames / n) : 0,
    sweep,
    musou: pulses * (pulse.damage || 0),
  };
}

/**
 * Absolute reference bands: [value that scores 1, value that scores 10].
 * Chosen to bracket the whole moveset table with headroom at both ends so a
 * future weapon can genuinely top or bottom a bar. Ten is not "the best of
 * these three" — it is the top of the scale the game is designed inside.
 */
const BANDS = {
  attack: [40, 110],    // light-chain damage per second
  reach: [2.0, 5.0],    // metres, longest swept radius in the chain
  speed: [1.0, 2.6],    // chain links per second
  sweep: [6, 16],       // metres of arc one blow travels
  musou: [400, 1100],   // total damage of one full musou
};

/** Every set measured once at module load; the moveset table is a constant. */
const STATS = (() => {
  const out = {};
  for (const n of Object.keys(MOVESETS)) {
    const raw = measure(n);
    const bars = { raw };
    for (const k of STAT_KEYS) {
      const [lo, hi] = BANDS[k];
      const t = (raw[k] - lo) / (hi - lo);
      bars[k] = Math.round(Math.max(0.5, Math.min(10, 1 + t * 9)) * 10) / 10;
    }
    out[n] = bars;
  }
  return out;
})();

/** The five measured bars for a moveset, 0..10. */
export function statsFor(setName) { return STATS[setName] || STATS.greatGlaive; }
export { STAT_KEYS, STAT_LABEL };

/**
 * The named moves a card shows. Read straight off the moveset table so the
 * front end can never advertise a finisher the weapon does not have — the
 * labels here are the same strings `combat.js` resolves.
 */
export function signatureFor(setName) {
  const S = MOVESETS[setName];
  if (!S) return { chain: [], finisher: '', musou: '' };
  const chain = S.chain.map(id => S.moves[id]?.label).filter(Boolean);
  return {
    chain,
    finisher: chain.length ? chain[chain.length - 1] : '',
    musou: S.moves[S.musou?.finish]?.label || '',
  };
}

/* ================================================================= WARRIORS */

/**
 * Three warriors, one per moveset. They are the same rigged body — the game
 * generates every vertex in code and there is exactly one hero rig — so what
 * separates them is what the player can actually feel: the steel in their
 * hands, the moveset that steel drives, and the colours the front end paints
 * them in. Anything else would be a costume the battle does not honour.
 */
export const HEROES = [
  {
    id: 'aryabhumi',
    name: 'Arya Bhumi',
    title: 'Wilwatikta Vanguard',
    kingdom: 'Wilwatikta',
    epithet: 'The Standing Gate',
    moveset: 'greatGlaive',
    weapon: 'greatGlaive',
    offhand: null,
    // Named for the mesh that is actually in his hands. A kudi is the broad
    // single-edged Sundanese polearm blade; `weapon: 'greatGlaive'` is the
    // geometry key and does not move.
    weaponLabel: 'Kudi Agung',
    // Portrait plate + card accents. Drawn by ui/gfx.js portrait(); no assets.
    art: { bg0: '#6a3722', bg1: '#331913', plume: 'rgba(198,52,34,.92)', helm: ['#d8b46a', '#7a5825', '#2c2010', '#6a5024'] },
    accent: '#e4bd72',
    line: 'Holds the line alone and dares the field to move them.',
    note: 'The hardest-hitting chain and the deepest musou on the roster, paid for with the slowest links. Slow to start; impossible to stop once it is running.',
  },
  {
    id: 'panjianom',
    name: 'Panji Anom',
    title: 'Lance of the Brantas',
    kingdom: 'Wilwatikta',
    epithet: 'River Rider',
    moveset: 'spear',
    weapon: 'spear',
    offhand: null,
    weaponLabel: 'Tombak',
    // The putih half of the banner: bone and silver against Arya Bhumi's red
    // and Sekar Taji's jade, so the three read apart while all flying gula
    // kelapa.
    art: { bg0: '#5e3a24', bg1: '#2a1811', plume: 'rgba(242,231,207,.92)', helm: ['#f2e7cf', '#9a8a6a', '#2c2418', '#b8a684'] },
    accent: '#f2e7cf',
    line: 'Crosses the field inside a single breath and arrives point-first.',
    note: 'The longest reach and the widest footprint of any set — one sweep clears more ground than the kudi does — and every strike travels with the wielder.',
  },
  {
    id: 'sekartaji',
    name: 'Sekar Taji',
    title: 'Twin Shadow',
    kingdom: 'Wilwatikta',
    epithet: 'Two Blades, One Breath',
    moveset: 'twinSabres',
    weapon: 'dao',
    offhand: 'dao',
    // Paired goloks, not kerises: the mesh is a single-edged sabre blade.
    weaponLabel: 'Golok Kembar',
    art: { bg0: '#26482f', bg1: '#101e17', plume: 'rgba(120,200,140,.92)', helm: ['#cfe4c6', '#4e7355', '#141d16', '#5c7f60'] },
    accent: '#9fc8a4',
    line: 'Faster than the eye can count and never in one place long.',
    note: 'Nearly twice the kudi’s cycle rate, at half its reach. Trades the polearm’s wall of steel for a musou that pulses every five frames.',
  },
  {
    id: 'bimasakti',
    name: 'Bima Sakti',
    title: 'Breaker of Gates',
    kingdom: 'Wilwatikta',
    epithet: 'The Falling Mountain',
    moveset: 'gada',
    weapon: 'gada',
    offhand: null,
    // The gada is Bima's weapon in the wayang, which is why the fourth warrior
    // on this roster carries his name rather than a new one.
    weaponLabel: 'Gada Rujakpala',
    art: { bg0: '#3a2c48', bg1: '#181222', plume: 'rgba(158,126,214,.92)', helm: ['#cbb6e6', '#5f4a7e', '#191225', '#7a6199'] },
    accent: '#b79ddb',
    line: 'Does not go around a raised shield. Goes through it.',
    note: 'The shortest reach and the slowest recovery on the roster, traded for the only chain that swings at ankle height and five moves that break a guard outright.',
  },
  {
    id: 'nyiratih',
    name: 'Nyi Ratih',
    title: 'Keeper of the Coils',
    kingdom: 'Wilwatikta',
    epithet: 'The Serpent at the Gate',
    moveset: 'cambuk',
    weapon: 'cambuk',
    offhand: null,
    // The naga is everywhere in Javanese temple carving; a chain with a
    // serpent's head on it needs no more explanation than the gada did.
    weaponLabel: 'Cambuk Naga',
    art: { bg0: '#1e4448', bg1: '#0d1e21', plume: 'rgba(96,204,196,.92)', helm: ['#bfe6e0', '#3f7a76', '#0f1d1e', '#5e9a95'] },
    accent: '#8fd4cb',
    line: 'Owns the ground you have to cross to reach her.',
    note: 'The longest reach in the game and the only weapon with an inner dead zone — every arc opens at 1.1 m, so a man who closes is safe from it. Controls more ground than anything else and is helpless once you are inside the chain.',
  },
  {
    id: 'kidangwesi',
    name: 'Kidang Wesi',
    title: 'Breaker of the Inner Gate',
    kingdom: 'Wilwatikta',
    epithet: 'Iron Deer',
    moveset: 'kapak',
    weapon: 'kapak',
    // Paired, so the roster row has to say so too — a moveset that carries an
    // offhand and a hero who does not is how the twin sabres once ended up
    // being swung as a single polearm.
    offhand: 'kapak',
    weaponLabel: 'Kapak Garuda',
    art: { bg0: '#4a3520', bg1: '#1d1409', plume: 'rgba(224,168,86,.92)', helm: ['#e8cfa4', '#8a6532', '#1c1408', '#a98247'] },
    accent: '#d9a75c',
    line: 'Has to be close. Only has to be close once.',
    /* Every number in this note is measured: the 2.4 m wall from
     * deadzone_sweep (43/43 pinned-dummy trials) and the officer kill from
     * duel_guard_census, reproduced across two runs. The cambuk's row above
     * describes its inverse, which is the point of granting them in sequence. */
    note: 'The shortest reach in the game — its chain dies at 2.4 m where every other weapon reaches 3.1 to 4.6 — and the fastest hands, opening in six frames on the lowest recovery of any set. Kills an officer in half the time the glaive needs, on a sixth of the hits.',
  },
];

/* =============================================================== BATTLEFIELDS */

const siteAt = (id) => SITES.find(s => s.id === id) || null;

/**
 * `stars` is the difficulty pip count the card shows. `pressure` multiplies
 * the mission's own phase ladder, `foeHp` the named officers' health, so a
 * five-star stage is genuinely harder rather than merely labelled so.
 */
/**
 * ---------------------------------------------------------------------------
 * ARENAS AND STAGES ARE NOT THE SAME THING
 * ---------------------------------------------------------------------------
 * This table used to be called ARENAS, which was wrong and hid the shape of
 * the game. An arena is a battlefield carved at boot from `?arena=`
 * (world/arenas.js): Lembah Brantas's river valley, Kraton Wilwatikta's walled
 * courts. The entries below are SORTIES onto one of them — `arena` says which
 * — with different light, a different landing point, different holdings
 * contested, a different push. Four are fought across the Brantas.
 *
 *   ARENA   a battlefield. Terrain + layout + props + architecture. Adding one
 *           is real work (see ARENAS below for what it costs).
 *   STAGE   a deployment onto an arena. Mood, spawn, seed, difficulty, orders.
 *           Adding one is a record in this table.
 *
 * Naming them apart is what lets Arena II exist later without the front end
 * having to be rebuilt around it.
 */
export const ALL_STAGES = [
  {
    id: 'crossing',
    arena: 'brantas',
    name: 'The Brantas Crossing',
    region: 'Lembah Brantas — the ferry at Tambangan',
    mood: 'golden',
    hour: 'Late afternoon',
    weather: 'Clear, low sun',
    stars: 2,
    difficulty: 'Vanguard',
    seed: 20240819,
    spawn: { x: 0, z: 30 },
    focus: 'bridge',
    sites: ['bridge', 'fort_north', 'fort_south'],
    brief: 'Segara Kidul has thrown its vanguard across the Brantas. Take the crossing before the ferry is theirs and the valley is cut in two.',
    pressure: 1.0,
    foeHp: 1.0,
  },
  {
    id: 'gapura',
    arena: 'brantas',
    // 19 chars: the rail card ellipsizes at nowrap (style.js .cn b), and
    // 'Storming Gapura Wringin' truncated to 'Storming Gapura Wri...'.
    name: 'The Wringin Assault',
    region: 'Lembah Brantas — the north gate',
    mood: 'storm',
    hour: 'Storm-dark noon',
    weather: 'Rain-front, flat light',
    stars: 3,
    difficulty: 'Veteran',
    seed: 771103,
    spawn: { x: 0, z: -110 },
    focus: 'fort_north',
    sites: ['fort_north', 'outpost', 'camp_east'],
    brief: 'The banyan gate has stood a hundred years and Segara Kidul holds it now. March up the north road under the storm and break the wall.',
    pressure: 1.18,
    foeHp: 1.25,
  },
  {
    id: 'kelud',
    arena: 'brantas',
    name: 'The Kelud Command',
    region: 'Lembah Brantas — the eastern high ground',
    mood: 'dusk',
    hour: 'Last light',
    weather: 'Clearing, long shadows',
    stars: 4,
    difficulty: 'Renowned',
    seed: 480921,
    spawn: { x: 60, z: 108 },
    focus: 'hill_post',
    sites: ['hill_post', 'camp_east', 'fort_south'],
    brief: 'Whoever holds the ridge sees the whole valley. Climb the eastern road at last light and take the command post off them.',
    pressure: 1.3,
    foeHp: 1.4,
  },
  {
    id: 'trowulan',
    arena: 'brantas',
    name: 'Trowulan by Night',
    region: 'Lembah Brantas — the southern bastion',
    mood: 'night-fire',
    hour: 'Third watch',
    weather: 'Moonless, burning camps',
    stars: 5,
    difficulty: 'Peerless',
    seed: 30612,
    spawn: { x: 8, z: 132 },
    focus: 'fort_south',
    sites: ['fort_south', 'hill_post', 'camp_west'],
    brief: 'They came in the dark for the bastion at our backs. There is no line left to hold — only the fires, and whoever is still standing at dawn.',
    pressure: 1.42,
    foeHp: 1.6,
  },
  {
    id: 'kraton',
    arena: 'kraton',
    name: 'The Palace Gates',
    region: 'Kraton Wilwatikta — the Gapura Agung',
    mood: 'dusk',
    hour: 'Last light',
    weather: 'Still air, smoke over the courts',
    stars: 3,
    difficulty: 'Veteran',
    seed: 918273,
    // inside the Gapura Agung, in the forecourt: every holding worth having
    // is at least one gate away, which is the whole of this ground's argument
    spawn: { x: 0, z: 186 },
    focus: 'court_west',
    sites: ['court_west', 'court_east', 'keep'],
    brief: 'The Kidul host came over the walls at first light and holds the inner courts. Break the gates one court at a time and take the Siti Hinggil back.',
    pressure: 1.2,
    foeHp: 1.3,
  },
  {
    id: 'alas',
    arena: 'alas',
    name: 'The Alas Purwo Raid',
    region: 'Alas Purwo — the Kidul war camp',
    mood: 'night-fire',
    hour: 'Second watch',
    weather: 'Moonless, woodsmoke under the trees',
    stars: 4,
    difficulty: 'Renowned',
    seed: 5150917,
    // in the forest below the plateau, out of sight of the stockade
    spawn: { x: 0, z: 168 },
    focus: 'stores',
    sites: ['stores', 'bivouac_w', 'command'],
    brief: 'The Kidul host has made camp on the old hill in the forest and sleeps behind its stockade. Go up under the trees, fire the granary, and cut down their command before the camp can form.',
    pressure: 1.3,
    foeHp: 1.4,
  },
];

/**
 * The sorties on the ground that is LOADED. An arena is carved at boot from
 * `?arena=` (world/layout.js), so a stage on another arena cannot be fought
 * without a reload — the front end lists these and offers the others as a
 * change of field. Every arena must have one; one that does not gets a
 * default sortie so the front end never lists nothing, and says so loudly.
 */
export const STAGES = (() => {
  const here = ALL_STAGES.filter((s) => s.arena === ARENA_ID);
  if (here.length) return here;
  const def = ARENA_DEFS.find((a) => a.id === ARENA_ID);
  console.warn(`[roster] arena "${ARENA_ID}" has no stages — using a default sortie`);
  return [{ ...ALL_STAGES[0], id: ARENA_ID, arena: ARENA_ID, name: def ? def.name : ARENA_ID,
    region: def ? def.name : ARENA_ID, focus: null, sites: [], spawn: { x: 0, z: 30 } }];
})();

/* =================================================================== ARENAS */

/**
 * The battlefields, as the front end describes them.
 *
 * `terrain` is the world arena id (`src/world/arenas.js`) whose record carves
 * it — heightfield, layout, walls, flora; `sites` is how many capturable
 * holdings that record carries. An arena needs all of: a landform, a
 * hand-authored layout, a flora rule, and its own architecture.
 */
const sitesOf = (id) => (ARENA_DEFS.find((a) => a.id === id)?.sites || []).length;

export const ARENA_1 = {
  id: 'arena1',
  index: 1,
  numeral: 'I',
  name: 'Lembah Brantas',
  status: 'prototype',
  terrain: 'brantas',          // the world arena id (world/arenas.js)
  span: 520,
  sites: sitesOf('brantas'),
  note: 'The prototype ground. One authored heightfield, four stages fought across it.',
  get stages() { return ALL_STAGES.filter((s) => s.arena === 'brantas'); },
};

export const ARENA_2 = {
  id: 'arena2',
  index: 2,
  numeral: 'II',
  name: 'Kraton Wilwatikta',
  status: 'new',
  terrain: 'kraton',
  span: 416,                   // the curtain, wall to wall
  sites: sitesOf('kraton'),
  note: 'The palace compound. Brick courts, split gates, and nothing between them but the gates.',
  get stages() { return ALL_STAGES.filter((s) => s.arena === 'kraton'); },
};

export const ARENA_3 = {
  id: 'arena3',
  index: 3,
  numeral: 'III',
  name: 'Alas Purwo',
  status: 'new',
  terrain: 'alas',
  span: 520,
  sites: sitesOf('alas'),
  note: 'A night raid. A war camp behind a timber stockade on a hill in the forest.',
  get stages() { return ALL_STAGES.filter((s) => s.arena === 'alas'); },
};

export const ARENAS = [ARENA_1, ARENA_2, ARENA_3];

/** The arena that is loaded — what the HUD and the front end must describe. */
export const ACTIVE_ARENA = ARENAS.find((a) => a.terrain === ARENA_ID) || ARENA_1;

/** The arena a stage is fought on. */
export function arenaOf(stage) {
  return ARENAS.find((a) => a.terrain === (stage && stage.arena)) || ACTIVE_ARENA;
}

/* ================================================================== lookups */

/**
 * Who the player is marching against, read off the mission script itself.
 * The battle is one authored ladder, so every stage faces the same command —
 * naming them here from `mission.js` means the briefing can never advertise a
 * general the script does not spawn.
 */
export const FOE_COMMAND = {
  general: FOE.general.name + ', ' + FOE.general.title,
  officers: [FOE.captainA, FOE.captainB, FOE.lieut].map(f => f.name).join(' · '),
};

export function heroById(id) { return HEROES.find(h => h.id === id) || HEROES[0]; }
export function stageById(id) { return STAGES.find(a => a.id === id) || STAGES[0]; }
export function heroIndex(id) { const i = HEROES.findIndex(h => h.id === id); return i < 0 ? 0 : i; }
export function stageIndex(id) { const i = STAGES.findIndex(a => a.id === id); return i < 0 ? 0 : i; }

/* The names the public API and docs/CONTRACTS.md already use. Kept pointing at
 * the stage table so nothing that calls them has to know about the rename. */
export const arenaById = stageById;
export const arenaIndex = stageIndex;

/** World-space points of the holdings a stage contests, for the stage map. */
export function arenaSites(arena) {
  const out = [];
  for (const id of arena.sites || []) { const s = siteAt(id); if (s) out.push(s); }
  return out;
}

/* ================================================================== apply */

/**
 * Put a warrior in the player's hands RIGHT NOW, without touching the battle.
 * Warrior select calls this as the highlight moves, so the man orbiting on
 * screen is holding the weapon the card is describing.
 */
export function previewHero(w, hero) {
  if (!w || !hero) return false;
  let ok = false;
  try { ok = !!w.player?.setWeapon?.(hero.moveset); } catch (e) { /* pre-init */ }
  try { w.chars?.setPlayerWeapon?.(hero.weapon, { offhand: hero.offhand }); } catch (e) { /* pre-init */ }
  try { w.ui?.setWarrior?.(hero); } catch (e) { /* HUD not built */ }
  return ok;
}

/**
 * Commit both choices. Called once, on "Begin the Battle", BEFORE the UI
 * state goes to 'playing' — `mission.start()` fires on the first frame the UI
 * reports 'playing', so everything it reads has to be in place by then.
 */
export function applyLoadout(w, heroId, arenaId) {
  const hero = heroById(heroId);
  const arena = arenaById(arenaId);
  if (!w) return { hero, arena };

  // The hero: moveset, steel, name plate, and the spawn the next reset uses.
  try {
    w.player?.setLoadout?.({
      weapon: hero.moveset,
      name: hero.name,
      title: hero.title,
      spawn: arena.spawn,
    });
  } catch (e) { /* pre-init */ }
  previewHero(w, hero);

  // The stage: put the hero where the battle is, then light it.
  try {
    const e = w.entities, id = w.playerId;
    if (e && id >= 0 && arena.spawn) {
      const T = w.terrain;
      const y = T ? (T.walkHeightAt ? T.walkHeightAt(arena.spawn.x, arena.spawn.z) : T.heightAt(arena.spawn.x, arena.spawn.z)) : 0;
      e.x[id] = arena.spawn.x; e.z[id] = arena.spawn.z; e.y[id] = y;
      e.vx[id] = 0; e.vy[id] = 0; e.vz[id] = 0;
    }
  } catch (err) { /* terrain not up */ }
  try { w.lighting?.setMood?.(arena.mood); } catch (e) { /* renderer not up */ }
  try { w.mission?.setArena?.(arena); } catch (e) { /* mission not up */ }
  try { w.ui?.setArena?.(arena); } catch (e) { /* HUD not built */ }

  return { hero, arena };
}
